/**
 * 订单详情页面 - 中式典雅风格（移动端优化）
 * 配色：宣纸白 (#F8F6F1)、茶褐 (#7D6B55)、竹青 (#6B8E7C)、朱砂红 (#C44536)
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  List,
  Skeleton,
  Divider,
  Tag,
  Button,
  message,
  Space,
  Typography,
  Row,
  Col,
  Grid,
  Table,
} from 'antd';
import {
  ArrowLeftOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import { orderApi } from '../../api/index';
import StorefrontLayout from '../../components/storefront/StorefrontLayout';

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

// 中式配色
const COLORS = {
  paper: '#F8F6F1',
  teaBrown: '#7D6B55',
  bambooGreen: '#6B8E7C',
  cinnabar: '#C44536',
  ink: '#2C2C2C',
  lightInk: '#5A5A5A',
  lightBorder: '#E0D8D0',
};

// 订单状态映射
const STATUS_MAP = {
  pending: { color: '#D4B483', label: '待付款' },
  paid: { color: COLORS.bambooGreen, label: '已付款' },
  processing: { color: '#6B8E9F', label: '处理中' },
  shipped: { color: '#7D6B9F', label: '已发货' },
  delivered: { color: COLORS.bambooGreen, label: '已送达' },
  completed: { color: COLORS.bambooGreen, label: '已完成' },
  cancelled: { color: COLORS.cinnabar, label: '已取消' },
};

// 财务状态映射
const FINANCIAL_STATUS_MAP = {
  pending: { color: '#D4B483', label: '待支付' },
  paid: { color: COLORS.bambooGreen, label: '已支付' },
  refunded: { color: COLORS.cinnabar, label: '已全额退款' },
  partially_refunded: { color: '#B95C5C', label: '部分退款' },
};

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchOrder = async () => {
    try {
      const data = await orderApi.getOrder(id);
      setOrder(data);
    } catch (err) {
      message.error('订单不存在');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  const renderStatus = (status) => {
    const config = STATUS_MAP[status] || { color: COLORS.lightInk, label: status };
    return (
      <Tag
        style={{
          background: `${config.color}15`,
          borderColor: config.color,
          color: config.color,
        }}
      >
        {config.label}
      </Tag>
    );
  };

  const renderFinancialStatus = (status) => {
    const config = FINANCIAL_STATUS_MAP[status] || { color: COLORS.lightInk, label: status || '未知' };
    return (
      <Tag
        style={{
          background: `${config.color}15`,
          borderColor: config.color,
          color: config.color,
        }}
      >
        {config.label}
      </Tag>
    );
  };

  const formatPrice = (amount) => `$${(amount || 0).toFixed(2)}`;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <StorefrontLayout>
        <div style={{ padding: isMobile ? '24px 12px' : '48px 24px' }}>
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      </StorefrontLayout>
    );
  }

  if (!order) return null;

  // 计算退款相关数据
  const refundedAmount = order.refundedAmount || 0;
  const hasRefund = refundedAmount > 0;
  const payments = order.payments || [];

  return (
    <StorefrontLayout>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: isMobile ? '16px 12px 32px' : '32px 24px 64px' }}>
        {/* 返回按钮 */}
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/orders')}
          style={{
            marginBottom: isMobile ? 16 : 24,
            padding: 0,
            color: COLORS.teaBrown,
            fontSize: isMobile ? 13 : 14,
          }}
        >
          返回订单列表
        </Button>

        {/* 订单状态卡片 */}
        <Card
          style={{
            marginBottom: isMobile ? 12 : 20,
            borderRadius: 12,
            border: 'none',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          }}
          bodyStyle={{ padding: isMobile ? 16 : 24 }}
        >
          {/* 订单编号 */}
          <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
            <Col>
              <Text style={{ fontSize: 13, color: COLORS.lightInk }}>订单编号</Text>
              <div style={{ marginTop: 4 }}>
                <Text strong style={{ color: COLORS.ink, fontFamily: 'monospace', fontSize: isMobile ? 14 : 16 }}>
                  {order.orderNo}
                </Text>
              </div>
            </Col>
            {!isMobile && (
              <Col style={{ textAlign: 'right' }}>
                <Text style={{ fontSize: 13, color: COLORS.lightInk }}>下单时间</Text>
                <div style={{ marginTop: 4 }}>
                  <Text style={{ color: COLORS.lightInk, fontSize: 14 }}>
                    {formatDate(order.createdAt)}
                  </Text>
                </div>
              </Col>
            )}
          </Row>

          {/* 移动端：下单时间单独一行 */}
          {isMobile && (
            <Row style={{ marginBottom: 12 }}>
              <Col>
                <Text style={{ fontSize: 12, color: COLORS.lightInk }}>
                  下单时间：{formatDate(order.createdAt)}
                </Text>
              </Col>
            </Row>
          )}

          <Divider style={{ margin: isMobile ? '12px 0' : '16px 0', borderColor: COLORS.lightBorder }} />

          {/* 订单总额和状态 */}
          <Row justify="space-between" align="middle" style={{ marginBottom: 12 }} wrap>
            <Col>
              <Text strong style={{ fontSize: isMobile ? 16 : 18, color: COLORS.ink }}>
                订单总额
              </Text>
            </Col>
            <Col xs={24} sm={12} style={{ textAlign: 'right', marginTop: isMobile ? 8 : 0 }}>
              <Space size={8} wrap direction={isMobile ? 'horizontal' : 'horizontal'}>
                <Text
                  strong
                  style={{
                    fontSize: isMobile ? 22 : 28,
                    fontWeight: 600,
                    color: COLORS.cinnabar,
                  }}
                >
                  {formatPrice(order.total)}
                </Text>
                {renderStatus(order.status)}
              </Space>
            </Col>
          </Row>

          {/* 财务状态（退款相关） */}
          <Row justify="space-between" align="middle" wrap>
            <Col>
              <Space size={4}>
                <DollarOutlined style={{ color: COLORS.lightInk }} />
                <Text style={{ fontSize: isMobile ? 13 : 14, color: COLORS.lightInk }}>支付状态</Text>
              </Space>
            </Col>
            <Col>
              <Space size={8}>
                {renderFinancialStatus(order.financialStatus)}
                {hasRefund && (
                  <Text type="secondary" style={{ fontSize: isMobile ? 12 : 13 }}>
                    已退 {formatPrice(refundedAmount)}
                  </Text>
                )}
              </Space>
            </Col>
          </Row>
        </Card>

        {/* 退款信息卡片 - 如果有退款 */}
        {hasRefund && (
          <Card
            title={
              <Space>
                <DollarOutlined style={{ color: COLORS.cinnabar }} />
                <Text strong style={{ fontSize: isMobile ? 14 : 16, color: COLORS.ink }}>
                  退款信息
                </Text>
              </Space>
            }
            style={{
              marginBottom: isMobile ? 12 : 20,
              borderRadius: 12,
              border: 'none',
              boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
              borderColor: COLORS.cinnabar,
            }}
            headStyle={{ borderBottom: `1px solid ${COLORS.lightBorder}`, padding: isMobile ? '12px 16px' : '16px 24px' }}
            bodyStyle={{ padding: isMobile ? 12 : 16 }}
          >
            <Row justify="space-between" style={{ marginBottom: 8 }}>
              <Col>
                <Text style={{ color: COLORS.lightInk, fontSize: isMobile ? 13 : 14 }}>已退款金额</Text>
              </Col>
              <Col>
                <Text strong style={{ color: COLORS.cinnabar, fontSize: isMobile ? 14 : 16 }}>
                  {formatPrice(refundedAmount)}
                </Text>
              </Col>
            </Row>
            <Row justify="space-between" style={{ marginBottom: 8 }}>
              <Col>
                <Text style={{ color: COLORS.lightInk, fontSize: isMobile ? 13 : 14 }}>剩余金额</Text>
              </Col>
              <Col>
                <Text strong style={{ color: COLORS.bambooGreen, fontSize: isMobile ? 14 : 16 }}>
                  {formatPrice(order.total - refundedAmount)}
                </Text>
              </Col>
            </Row>
            <Divider style={{ margin: '8px 0', borderColor: COLORS.lightBorder }} />
            <Row>
              <Col>
                <Text type="secondary" style={{ fontSize: isMobile ? 12 : 13 }}>
                  如有疑问，请联系客服处理退款事宜
                </Text>
              </Col>
            </Row>
          </Card>
        )}

        {/* 收货地址 */}
        <Card
          title={
            <Space>
              <EnvironmentOutlined style={{ color: COLORS.teaBrown }} />
              <Text strong style={{ fontSize: isMobile ? 14 : 16, color: COLORS.ink }}>
                收货地址
              </Text>
            </Space>
          }
          style={{
            marginBottom: isMobile ? 12 : 20,
            borderRadius: 12,
            border: 'none',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          }}
          headStyle={{ borderBottom: `1px solid ${COLORS.lightBorder}`, padding: isMobile ? '12px 16px' : '16px 24px' }}
          bodyStyle={{ padding: isMobile ? 12 : 16 }}
          size={isMobile ? 'small' : 'default'}
        >
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Text strong style={{ color: COLORS.ink, fontSize: isMobile ? 14 : 15 }}>
              {order.shippingFirstName} {order.shippingLastName}
            </Text>
            {order.shippingPhone && (
              <Space size={4}>
                <PhoneOutlined style={{ color: COLORS.lightInk, fontSize: 12 }} />
                <Text style={{ color: COLORS.lightInk, fontSize: isMobile ? 13 : 14 }}>
                  {order.shippingPhone}
                </Text>
              </Space>
            )}
            <Text style={{ color: COLORS.lightInk, fontSize: isMobile ? 13 : 14 }}>
              {order.shippingAddress1}
              {order.shippingAddress2 && ` ${order.shippingAddress2}`}
            </Text>
            <Text style={{ color: COLORS.lightInk, fontSize: isMobile ? 13 : 14 }}>
              {[order.shippingCity, order.shippingProvince, order.shippingPostalCode]
                .filter(Boolean)
                .join(', ')}
            </Text>
            <Text style={{ color: COLORS.lightInk, fontSize: isMobile ? 13 : 14 }}>
              {order.shippingCountry}
            </Text>
          </Space>
        </Card>

        {/* 物流信息 - 如果已发货 */}
        {order.status === 'shipped' && order.trackingNumber && (
          <Card
            title={
              <Space>
                <CreditCardOutlined style={{ color: COLORS.teaBrown }} />
                <Text strong style={{ fontSize: isMobile ? 14 : 16, color: COLORS.ink }}>
                  物流信息
                </Text>
              </Space>
            }
            style={{
              marginBottom: isMobile ? 12 : 20,
              borderRadius: 12,
              border: 'none',
              boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
            }}
            headStyle={{ borderBottom: `1px solid ${COLORS.lightBorder}`, padding: isMobile ? '12px 16px' : '16px 24px' }}
            bodyStyle={{ padding: isMobile ? 12 : 16 }}
          >
            <Row style={{ marginBottom: 8 }}>
              <Col span={8}>
                <Text style={{ color: COLORS.lightInk, fontSize: isMobile ? 13 : 14 }}>物流公司</Text>
              </Col>
              <Col span={16}>
                <Text style={{ color: COLORS.ink, fontSize: isMobile ? 13 : 14 }}>
                  {order.trackingCarrier || '未指定'}
                </Text>
              </Col>
            </Row>
            <Row style={{ marginBottom: 8 }}>
              <Col span={8}>
                <Text style={{ color: COLORS.lightInk, fontSize: isMobile ? 13 : 14 }}>物流单号</Text>
              </Col>
              <Col span={16}>
                <Text style={{ fontFamily: 'monospace', fontSize: isMobile ? 13 : 14 }}>
                  {order.trackingNumber}
                </Text>
              </Col>
            </Row>
            {order.shippedAt && (
              <Row>
                <Col span={8}>
                  <Text style={{ color: COLORS.lightInk, fontSize: isMobile ? 13 : 14 }}>发货时间</Text>
                </Col>
                <Col span={16}>
                  <Text style={{ color: COLORS.ink, fontSize: isMobile ? 13 : 14 }}>
                    {formatDate(order.shippedAt)}
                  </Text>
                </Col>
              </Row>
            )}
          </Card>
        )}

        {/* 商品列表 */}
        <Card
          title={
            <Space>
              <ShoppingCartOutlined style={{ color: COLORS.teaBrown }} />
              <Text strong style={{ fontSize: isMobile ? 14 : 16, color: COLORS.ink }}>
                商品明细 ({(order.items || []).length} 件)
              </Text>
            </Space>
          }
          style={{
            marginBottom: isMobile ? 12 : 20,
            borderRadius: 12,
            border: 'none',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          }}
          headStyle={{ borderBottom: `1px solid ${COLORS.lightBorder}`, padding: isMobile ? '12px 16px' : '16px 24px' }}
          bodyStyle={{ padding: 0 }}
          size={isMobile ? 'small' : 'default'}
        >
          <List
            itemLayout="horizontal"
            dataSource={order.items}
            renderItem={(item, index) => (
              <List.Item
                style={{
                  padding: isMobile ? '12px 16px' : '16px 24px',
                  borderBottom: index < order.items.length - 1 ? `1px solid ${COLORS.lightBorder}` : 'none',
                }}
              >
                <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                  <Col flex={1}>
                    <Text strong style={{ color: COLORS.ink, fontSize: isMobile ? 14 : 15 }}>
                      {item.title}
                    </Text>
                    {item.variantTitle && (
                      <div style={{ marginTop: 4 }}>
                        <Tag
                          size="small"
                          style={{
                            background: `${COLORS.bambooGreen}15`,
                            borderColor: COLORS.bambooGreen,
                            color: COLORS.bambooGreen,
                            fontSize: 11,
                          }}
                        >
                          {item.variantTitle}
                        </Tag>
                      </div>
                    )}
                    <Text
                      type="secondary"
                      style={{ display: 'block', marginTop: 4, fontSize: isMobile ? 12 : 13 }}
                    >
                      数量：×{item.quantity} · 单价：{formatPrice(item.price)}
                    </Text>
                  </Col>
                  <Col>
                    <Text
                      strong
                      style={{
                        color: COLORS.cinnabar,
                        fontSize: isMobile ? 14 : 16,
                      }}
                    >
                      {formatPrice(item.price * item.quantity)}
                    </Text>
                  </Col>
                </Row>
              </List.Item>
            )}
          />
        </Card>

        {/* 价格明细 */}
        <Card
          title={
            <Space>
              <div
                style={{
                  width: 4,
                  height: 18,
                  background: COLORS.teaBrown,
                  borderRadius: 2,
                }}
              />
              <Text strong style={{ fontSize: isMobile ? 14 : 16, color: COLORS.ink }}>
                价格明细
              </Text>
            </Space>
          }
          style={{
            borderRadius: 12,
            border: 'none',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          }}
          headStyle={{ borderBottom: `1px solid ${COLORS.lightBorder}`, padding: isMobile ? '12px 16px' : '16px 24px' }}
          bodyStyle={{ padding: isMobile ? '12px 16px' : '16px 24px' }}
          size={isMobile ? 'small' : 'default'}
        >
          <div style={{ padding: '4px 0' }}>
            <Row justify="space-between" style={{ marginBottom: 8 }}>
              <Text style={{ color: COLORS.lightInk, fontSize: isMobile ? 13 : 14 }}>商品小计</Text>
              <Text style={{ color: COLORS.ink, fontSize: isMobile ? 13 : 14 }}>{formatPrice(order.subtotal)}</Text>
            </Row>
            {order.shippingFee > 0 && (
              <Row justify="space-between" style={{ marginBottom: 8 }}>
                <Text style={{ color: COLORS.lightInk, fontSize: isMobile ? 13 : 14 }}>运费</Text>
                <Text style={{ color: COLORS.ink, fontSize: isMobile ? 13 : 14 }}>{formatPrice(order.shippingFee)}</Text>
              </Row>
            )}
            {order.discountAmount > 0 && (
              <Row justify="space-between" style={{ marginBottom: 8 }}>
                <Text style={{ color: COLORS.lightInk, fontSize: isMobile ? 13 : 14 }}>优惠</Text>
                <Text style={{ color: COLORS.cinnabar, fontSize: isMobile ? 13 : 14 }}>
                  -{formatPrice(order.discountAmount)}
                </Text>
              </Row>
            )}
            {hasRefund && (
              <Row justify="space-between" style={{ marginBottom: 8 }}>
                <Text style={{ color: COLORS.cinnabar, fontSize: isMobile ? 13 : 14 }}>已退款</Text>
                <Text style={{ color: COLORS.cinnabar, fontSize: isMobile ? 13 : 14 }}>
                  -{formatPrice(refundedAmount)}
                </Text>
              </Row>
            )}
          </div>

          <Divider style={{ borderColor: COLORS.lightBorder, margin: '8px 0' }} />

          <Row justify="space-between" align="middle">
            <Text strong style={{ fontSize: isMobile ? 14 : 16, color: COLORS.ink }}>
              {hasRefund ? '实付金额' : '合计'}
            </Text>
            <Text
              strong
              style={{
                fontSize: isMobile ? 20 : 24,
                fontWeight: 700,
                color: COLORS.cinnabar,
              }}
            >
              {formatPrice(order.total - refundedAmount)}
            </Text>
          </Row>
        </Card>
      </div>
    </StorefrontLayout>
  );
}